'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { LocaleLink } from '@/core/i18n';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { usePageContent } from '@/core/i18n/content';
import { TEMPLATE_CHROME } from '@/core/i18n/content/template-chrome.content';
import type { TemplateChromeContent } from '@/core/i18n/content/template-chrome.types';
import { useTranslation } from '@/core/i18n';
import { BEST_SOFTWARE_TRANSLATIONS, type BestSoftwareText } from '@/core/i18n/content/best-software.content';
import { useLocaleContext } from '@/core/i18n/locale.context';
import { useLocaleStore } from '@/core/i18n/locale.store';

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

export interface BestSoftwareConfig {
  slug: string;
  heroTitle: string;
  heroSubtitle: string;
  rankings: {
    rank: number;
    name: string;
    rating: string;
    bestFor: string;
    pricing: string;
    highlights: string[];
    isHotelStack?: boolean;
  }[];
  evaluationCriteria: { name: string; description: string; weight: string }[];
  faqItems: { question: string; answer: string }[];
}

/* -------------------------------------------------------------------------- */
/*  SVG Icons                                                                 */
/* -------------------------------------------------------------------------- */

function StarSVG({ filled }: { filled: boolean }) {
  const { t } = useTranslation();
  return (
    <svg viewBox="0 0 16 16" fill={filled ? 'currentColor' : 'none'} className={cn('h-3.5 w-3.5', filled ? 'text-hms-gold' : 'text-hms-textDim')}>
      <path
        d="M8 1.5l1.85 3.75L14 5.88l-2.95 2.88.7 4.07L8 10.88l-3.75 1.95.7-4.07L2 5.88l4.15-.63L8 1.5z"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RatingStars({ rating }: { rating: string }) {
  const numRating = parseFloat(rating);
  const fullStars = Math.floor(numRating);
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <StarSVG key={i} filled={i < fullStars} />
      ))}
      <span className="ml-1.5 text-sm font-bold text-hms-text">{rating}</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Hero                                                            */
/* -------------------------------------------------------------------------- */

function RankingHero({ config, t }: { config: BestSoftwareConfig; t: TemplateChromeContent }) {
  return (
    <section id={`compare-${config.slug}-hero`} className="relative w-full overflow-hidden bg-hms-bg">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-4xl px-4 pb-16 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-40">
        <p id={`compare-${config.slug}-badge`} className="mb-4 text-xs font-semibold uppercase tracking-widest text-hms-gold">
          {t.ranking.badge}
        </p>
        <h1
          id={`compare-${config.slug}-title`}
          className={cn(
            'font-poppins text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {config.heroTitle}
        </h1>
        <p id={`compare-${config.slug}-subtitle`} className="mx-auto mt-6 max-w-2xl text-base text-hms-textSub sm:text-lg">
          {config.heroSubtitle}
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Rankings                                                        */
/* -------------------------------------------------------------------------- */

function RankingsTable({ config, t }: { config: BestSoftwareConfig; t: TemplateChromeContent }) {
  return (
    <section id={`compare-${config.slug}-rankings`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`compare-${config.slug}-rankings-title`}
          className="text-center text-2xl font-bold tracking-tight text-hms-text sm:text-3xl"
        >
          {t.ranking.top5}
        </h2>
        <div className="mt-12 space-y-5">
          {config.rankings.map((entry) => (
            <div
              key={entry.rank}
              id={`compare-${config.slug}-rank-${entry.rank}`}
              className={cn(
                'rounded-xl border p-6 transition-colors',
                entry.isHotelStack
                  ? 'border-hms-gold/40 bg-hms-card shadow-lg shadow-hms-gold/5'
                  : 'border-hms-border bg-hms-card hover:border-hms-border/80',
              )}
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                {/* Left: rank + info */}
                <div className="flex items-start gap-4">
                  <div
                    className={cn(
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-extrabold',
                      entry.isHotelStack
                        ? 'bg-hms-gold/20 text-hms-gold'
                        : 'bg-hms-surface text-hms-textSub',
                    )}
                  >
                    #{entry.rank}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2.5">
                      <h3 className={cn('text-base font-bold', entry.isHotelStack ? 'text-hms-gold' : 'text-hms-text')}>
                        {entry.name}
                      </h3>
                      {entry.isHotelStack && (
                        <span className="rounded-full bg-hms-gold/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-hms-gold">
                          {t.ranking.topPick}
                        </span>
                      )}
                    </div>
                    <RatingStars rating={entry.rating} />
                    <p className="mt-1.5 text-xs text-hms-textDim">{entry.bestFor}</p>
                  </div>
                </div>
                {/* Right: pricing */}
                <div className="text-left sm:text-right">
                  <p className={cn('text-lg font-extrabold', entry.isHotelStack ? 'text-hms-gold' : 'text-hms-text')}>
                    {entry.pricing}
                  </p>
                  <p className="text-xs text-hms-textDim">{t.ranking.perMonth}</p>
                </div>
              </div>
              {/* Highlights */}
              <div className="mt-4 flex flex-wrap gap-2">
                {entry.highlights.map((hl, hIdx) => (
                  <span
                    key={hIdx}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium',
                      entry.isHotelStack
                        ? 'bg-hms-gold/10 text-hms-gold'
                        : 'bg-hms-surface text-hms-textSub',
                    )}
                  >
                    <svg viewBox="0 0 12 12" fill="none" className="h-2.5 w-2.5">
                      <path d="M3 6.5l2 2L9 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {hl}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Evaluation Criteria                                             */
/* -------------------------------------------------------------------------- */

function EvaluationCriteria({ config, t }: { config: BestSoftwareConfig; t: TemplateChromeContent }) {
  return (
    <section id={`compare-${config.slug}-criteria`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(200,145,58,0.04) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`compare-${config.slug}-criteria-title`}
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.ranking.howWeEvaluate}
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-center text-base text-hms-textSub">
          {t.ranking.evaluateDesc}
        </p>
        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {config.evaluationCriteria.map((crit, idx) => (
            <div
              key={crit.name}
              id={`compare-${config.slug}-criterion-${idx}`}
              className={cn(
                'group rounded-xl border border-hms-border bg-hms-card p-6',
                'transition-all duration-300 hover:border-hms-gold/20 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20',
              )}
            >
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-bold text-hms-text">{crit.name}</h3>
                <span className="rounded-full bg-hms-gold/10 px-2.5 py-0.5 text-xs font-bold text-hms-gold">
                  {crit.weight}
                </span>
              </div>
              <p className="text-xs leading-relaxed text-hms-textSub">{crit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — HotelStack Spotlight                                            */
/* -------------------------------------------------------------------------- */

function HotelStackSpotlight({ config, t }: { config: BestSoftwareConfig; t: TemplateChromeContent }) {
  const hs = config.rankings.find((r) => r.isHotelStack);
  if (!hs) return null;

  return (
    <section id={`compare-${config.slug}-spotlight`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`compare-${config.slug}-spotlight-title`}
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.ranking.whyFirst}
        </h2>
        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {t.ranking.differentiators.map((diff, idx) => (
            <div
              key={idx}
              id={`compare-${config.slug}-spotlight-${idx}`}
              className="flex items-start gap-3 rounded-xl border border-hms-border bg-hms-card p-4 transition-colors hover:border-hms-gold/20"
            >
              <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-hms-gold/10">
                <svg viewBox="0 0 16 16" fill="none" className="h-3 w-3 text-hms-gold">
                  <path d="M4 8.5l2.5 2.5L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <p className="text-sm leading-relaxed text-hms-textSub">{diff}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — FAQ                                                             */
/* -------------------------------------------------------------------------- */

function RankingFAQ({ config, t }: { config: BestSoftwareConfig; t: TemplateChromeContent }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const toggle = useCallback((i: number) => setOpenIndex((p) => (p === i ? null : i)), []);

  return (
    <section id={`compare-${config.slug}-faq`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`compare-${config.slug}-faq-title`}
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.common.faq}
        </h2>
        <div className="mt-12 space-y-3">
          {config.faqItems.map((item, idx) => (
            <div
              key={idx}
              id={`compare-${config.slug}-faq-item-${idx}`}
              className={cn(
                'rounded-xl border transition-colors',
                openIndex === idx ? 'border-hms-gold/30 bg-hms-card' : 'border-hms-border bg-hms-card/50 hover:border-hms-gold/15',
              )}
            >
              <button
                id={`compare-${config.slug}-faq-toggle-${idx}`}
                type="button"
                onClick={() => toggle(idx)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                aria-expanded={openIndex === idx}
              >
                <span className={cn('text-sm font-semibold leading-snug', openIndex === idx ? 'text-hms-gold' : 'text-hms-text')}>
                  {item.question}
                </span>
                <ChevronDown className={cn('h-4 w-4 shrink-0 transition-transform duration-200', openIndex === idx ? 'rotate-180 text-hms-gold' : 'text-hms-textDim')} />
              </button>
              {openIndex === idx && (
                <div className="px-5 pb-5">
                  <p className="text-sm leading-relaxed text-hms-textSub">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — CTA                                                             */
/* -------------------------------------------------------------------------- */

function RankingCTA({ config, t }: { config: BestSoftwareConfig; t: TemplateChromeContent }) {
  return (
    <section id={`compare-${config.slug}-cta`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(200,145,58,0.06) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <h2
          id={`compare-${config.slug}-cta-title`}
          className={cn(
            'text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.ranking.tryCta}
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base text-hms-textSub sm:text-lg">
          {t.ranking.tryDesc}
        </p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id={`compare-${config.slug}-cta-trial`}
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {t.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink
            id={`compare-${config.slug}-cta-contact`}
            href="/contact"
            className={cn(
              'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-text',
              'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
              'w-full sm:w-auto',
            )}
          >
            {t.cta.contactBtn}
          </LocaleLink>
        </div>
        <p className="mt-5 text-xs text-hms-textDim">{t.common.noCreditCard}</p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Locale merge                                                              */
/* -------------------------------------------------------------------------- */

function mergeBestSoftwareConfig(
  cfg: BestSoftwareConfig,
  txt: BestSoftwareText | undefined,
): BestSoftwareConfig {
  if (!txt) return cfg;
  return {
    ...cfg,
    heroTitle: txt.heroTitle,
    heroSubtitle: txt.heroSubtitle,
    rankings: cfg.rankings.map((r, i) => ({
      ...r, // keeps rank, name, rating, pricing, isHotelStack
      bestFor: txt.rankings[i]?.bestFor ?? r.bestFor,
      highlights: txt.rankings[i]?.highlights ?? r.highlights,
    })),
    evaluationCriteria: cfg.evaluationCriteria.map((c, i) => ({
      ...c, // keeps weight
      name: txt.evaluationCriteria[i]?.name ?? c.name,
      description: txt.evaluationCriteria[i]?.description ?? c.description,
    })),
    faqItems: txt.faqItems,
  };
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                            */
/* -------------------------------------------------------------------------- */

export function BestSoftwareRanking({ config: baseConfig }: { config: BestSoftwareConfig }) {
  const t = usePageContent(TEMPLATE_CHROME);
  const ctxLocale = useLocaleContext();
  const storeLocale = useLocaleStore((s) => s.locale);
  const locale = ctxLocale ?? storeLocale;
  const localeText = locale !== 'en' ? BEST_SOFTWARE_TRANSLATIONS[locale] : undefined;
  const config = mergeBestSoftwareConfig(baseConfig, localeText);

  return (
    <main id={`compare-${config.slug}-page`}>
      <RankingHero config={config} t={t} />
      <RankingsTable config={config} t={t} />
      <EvaluationCriteria config={config} t={t} />
      <HotelStackSpotlight config={config} t={t} />
      <RankingFAQ config={config} t={t} />
      <RankingCTA config={config} t={t} />
    </main>
  );
}
